import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Chip, Box, IconButton } from "@mui/material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";

import { useTablePagination, useAuth } from "hooks";
import { InfoOutlined } from "@mui/icons-material";
import { useLazyListCollectionForDriverQuery } from "store/services/collectionApi";

const getStatusStyle = (status) => {
  const styles = {
    COMPLETED: { backgroundColor: "#EBF5FB", textColor: "green" },
    CANCELLED: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
  };

  return (
    styles[status?.toUpperCase()] || {
      backgroundColor: "#F8F9F9",
      textColor: "#7F8C8D",
    }
  );
};

const DriverCollectionListScreen = () => {
  const { loggedInUser } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";
  const breadcrumbs = [{ title: "Collections" }];


  const columns = [
    {
      name: "Collection ID",
      selector: (row) => row.collectionId,
      cell: (row) => (
        <Link
          title={row?.collectionId}
          to={`/app/driver/collection/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.collectionId ? (
            <HighlightedText text={row?.collectionId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Shipment ID",
      cell: (row) => {
        const shipments = Array.isArray(row?.shipmentrequest)
          ? row.shipmentrequest
          : [];
        const validShipmentIds = shipments
          .filter((shipment) => shipment?.shipmentId)
          .map((shipment) => shipment?.shipmentId)
          .filter(Boolean);

        return validShipmentIds.length > 2
          ? validShipmentIds.slice(0, 2).join(", ") + ", ..."
          : validShipmentIds.join(", ") || "N/A";
      },
    },
    {
      name: "Vehicle ID",
      selector: (row) => row.vehicle?.vehicle_id || "N/A",
      cell: (row) => row.vehicle?.vehicle_id || "N/A",
    },
    {
      name: "Contractor ID",
      selector: (row) => row.contractorId || "N/A",
      cell: (row) => (
        <div>
          <span className="text-sm">{row.contractor?.name || "N/A"}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row.contractor?.contractorId || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "Pickup Time",
      selector: (row) => <AppTableDateTime date={row?.actualPickupTime} />,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Chip
            label={row?.status?.toUpperCase() || "N/A"}
            sx={{
              backgroundColor: getStatusStyle(row?.status).backgroundColor,
              color: getStatusStyle(row?.status).textColor,
              fontWeight: "bold",
              height: "17px",
              borderRadius: "12px",
              width: "auto",
              padding: "0",
              border: "none",
              "& .MuiChip-label": {
                fontSize: "0.6rem",
                padding: "3px 5px",
                lineHeight: "14px",
              },
            }}
          />
        </Box>
      ),
      center: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/driver/collection/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Shipment Unloading"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchFulfillments,
    { data: fulfillmentRes, isLoading, isFetching, error },
  ] = useLazyListCollectionForDriverQuery();

  React.useEffect(() => {
    if (
      loggedInUser &&
      loggedInUser.typeRef &&
      loggedInUser.typeRefSource === "Driver"
    ) {
      fetchFulfillments({
        page: currentPage,
        limit: perPage,
        ...(keyword && { keyword }),
        driverId: loggedInUser.typeRef,
      });
    }
  }, [
    currentPage,
    fetchFulfillments,
    keyword,
    perPage,
    loggedInUser,
  ]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <TableFilter
          searchLabel="Search Fulfillments"
          disabled={isLoading || isFetching}
        />
        <AppTable
          columns={columns}
          data={fulfillmentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={fulfillmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DriverCollectionListScreen;